import {
  AppError,
  AuthError,
  ConflictError,
  ForbiddenError,
  NetworkError,
  NotFoundError,
  RateLimitedError,
  ServerError,
  TimeoutError,
  UnknownError,
  ValidationError,
  type AppErrorDetails,
} from './AppError';

export interface HttpErrorBody {
  message?: string;
  error?: string;
  code?: string;
  fields?: Record<string, string[]>;
  details?: Record<string, unknown>;
}

/**
 * Map a server-shaped error payload + HTTP status to an `AppError`.
 * Unknown statuses bucket into `ServerError` (5xx) or `UnknownError`.
 */
export function fromHttpError(
  status: number,
  body: HttpErrorBody | null | undefined,
  cause?: unknown,
): AppError {
  const message = body?.message ?? body?.error ?? defaultMessageForStatus(status);
  const details: AppErrorDetails = {
    code: body?.code,
    fields: body?.fields,
    meta: body?.details,
    cause,
  };

  if (status === 400 || status === 422) return new ValidationError(message, details);
  if (status === 401) return new AuthError(message, details);
  if (status === 403) return new ForbiddenError(message, details);
  if (status === 404) return new NotFoundError(message, details);
  if (status === 409) return new ConflictError(message, details);
  if (status === 429) return new RateLimitedError(message, details);
  if (status >= 500 && status < 600) return new ServerError(message, { status, details });
  return new UnknownError(message, details);
}

/**
 * Map low-level fetch/network exceptions (AbortError, TypeError from
 * `fetch`, etc.) to `AppError`.
 */
export function fromFetchError(err: unknown): AppError {
  if (err instanceof AppError) return err;
  if (err && typeof err === 'object' && 'name' in err) {
    const name = (err as { name: string }).name;
    if (name === 'AbortError') return new TimeoutError('Request aborted', { cause: err });
  }
  if (err instanceof TypeError) {
    return new NetworkError(err.message, { cause: err });
  }
  if (err instanceof Error) {
    return new UnknownError(err.message, { cause: err });
  }
  return new UnknownError('Unknown error', { cause: err });
}

function defaultMessageForStatus(status: number): string {
  if (status >= 500) return 'Server error';
  if (status === 429) return 'Too many requests';
  if (status === 409) return 'Conflict';
  if (status === 404) return 'Not found';
  if (status === 403) return 'Forbidden';
  if (status === 401) return 'Authentication required';
  if (status === 400) return 'Invalid request';
  return 'Request failed';
}
